import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { useServices } from "../../contexts/ServiceContext";
import { HorseLine, setPlaceColours } from "./HorseLine";
import { HorseRaceEvent, SubmitHorseRacePicks } from "@freeplay/shared-code";
import { GameActions } from "../../controllers/GameController";

export interface PickEmPropProps {
    propString: string;
    numberOfPlaces: number;
    currentPick: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: HorseRaceEvent;
    makeHorseRacePick: React.Dispatch<GameActions>;
    reviewPicksState?: boolean;
    dispatch: React.Dispatch<GameActions>;
    incomingSelections: SubmitHorseRacePicks;
}

export const PickEmProp = ({
    propString,
    numberOfPlaces,
    currentPick,
    event,
    makeHorseRacePick,
    reviewPicksState,
    dispatch,
    incomingSelections,
}: PickEmPropProps): JSX.Element => {
    // eslint-disable-next-line
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(generateStyles);

    const handleSelection = (selection: number) => {
        dispatch({
            type: "setHorsePickemSelection",
            promptIndex: currentPick,
            selection,
        });
    };

    const selectedHorseIndexes = incomingSelections?.choices ?? [];

    const displayPlaceCards = () => {
        const elements: JSX.Element[] = [];

        for (let i = 0; i < numberOfPlaces; ++i) {
            let backgroundColour, textColour;
            let saddleNumber = "";
            let selectedStyle = false;
            if (i < selectedHorseIndexes.length) {
                [backgroundColour, textColour] = setPlaceColours(selectedHorseIndexes[i]);
                saddleNumber = selectedHorseIndexes[i].toString();
                selectedStyle = true;
            }
            elements.push(
                <View key={i} style={styles.placeCard}>
                    <View style={[styles.placeCardHeader, selectedStyle ? styles.selected : {}]}>
                        <Text style={styles.placeHeaderText}>
                            {i + 1}
                            <Text style={styles.ordinalText}>{getOrdinalString(i + 1)}</Text>
                        </Text>
                    </View>
                    <View style={[styles.placeCardContent, selectedStyle ? styles.selectedBorder : {}]}>
                        <View style={[styles.placeCardContentInner, { backgroundColor: backgroundColour }]}>
                            <Text style={[styles.placeCardContentText, { color: textColour }]}>{saddleNumber}</Text>
                        </View>
                    </View>
                </View>,
            );
        }
        return elements;
    };

    return (
        <View style={[styles.prop]}>
            <Text style={styles.propString}>{propString}</Text>
            <View style={styles.placeContainer}>{displayPlaceCards()}</View>
            <View style={styles.horseContainer}>
                {event.horseData.map((entry, index) => (
                    <HorseLine
                        key={`horse-${index}`}
                        horseInfo={{
                            horseName: entry.horseName,
                            saddleNumber: entry.programNumber,
                            odds: entry.morningLine,
                        }}
                        reviewPicksState={reviewPicksState}
                        disabled={
                            selectedHorseIndexes.length === numberOfPlaces &&
                            !selectedHorseIndexes.includes(entry.programNumber)
                                ? true
                                : false
                        }
                        index={index}
                        isActive={selectedHorseIndexes.includes(entry.programNumber)}
                        onSelect={() => handleSelection(entry.programNumber)}
                    />
                ))}
            </View>
        </View>
    );
};

const getOrdinalString = (num: number) => {
    if (num <= 0) return "";

    switch (num % 100) {
        case 11:
        case 12:
        case 13:
            return "th";
    }

    switch (num % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

const generateStyles = () => {
    return EStyleSheet.create({
        prop: {
            width: "100%",
            minHeight: "30px",
            justifyContent: "center",
            alignItems: "center",
        },
        propString: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            fontSize: 12,
            marginBottom: 2,
        },
        placeContainer: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "row",
            flexWrap: "wrap",
        },
        placeCard: {
            width: 48,
            height: 62,
            margin: 2.5,
            marginTop: 0,
        },
        placeCardHeader: {
            height: 28,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND),
            borderTopLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            justifyContent: "center",
            alignItems: "center",
        },
        selected: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND_ACTIVE),
        },
        selectedBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND_ACTIVE),
        },
        placeHeaderText: {
            color: () => EStyleSheet.value(GlobalStyles.HR_PLACE_TEXT),
            fontWeight: "700",
            fontSize: 14,
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
        },
        ordinalText: {
            fontSize: 12,
        },
        placeCardContentText: {
            fontWeight: "700",
        },
        placeCardContent: {
            height: 34,
            borderColor: () => EStyleSheet.value(GlobalStyles.HR_PLACE_BACKGROUND),
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
        },
        placeCardContentInner: {
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: "#ffffff",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.HR_PLACE_CARD_BORDER_RADIUS),
        },
        horseContainer: {
            width: "100%",
        },
    });
};
